/**
 * Plan of Record Page Settings Store
 */

import { create } from 'zustand';
import {
  countVisiblePorPages,
  defaultPorLandingSegment,
  listPorPageSettings,
  listPorRecordPageSettings,
  resolvePorRecordPageVisibility,
  setPorPageVisible,
  setPorRecordPageVisible,
  visiblePorSectionOrder,
  type PorPageKey,
  type PorRecordPageSetting,
  type WorkspacePorPageSetting,
} from '../../lib/porPageSettingsService';
import type { PorSectionType } from '../../types/plan-of-record';

type LayoutMode = 'workspace' | 'record';

interface PorPageSettingsState {
  workspaceSettings: WorkspacePorPageSetting[];
  recordSettings: PorRecordPageSetting[];
  mode: LayoutMode;
  activePorId: string | null;
  loading: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;
  loadedPorId: string | null;

  fetchWorkspaceSettings: (workspaceId: string) => Promise<void>;
  fetchRecordSettings: (porId: string, workspaceId: string) => Promise<void>;
  setMode: (mode: LayoutMode, porId?: string | null) => void;
  isVisible: (pageKey: PorPageKey) => boolean;
  layoutSource: () => 'record' | 'workspace' | 'default';
  visibleSectionOrder: () => PorSectionType[];
  defaultLandingSegment: () => string;
  visiblePageCount: () => number;
  toggleVisible: (pageKey: PorPageKey, workspaceId: string, porId?: string | null) => Promise<void>;
}

function effectiveVisibility(state: PorPageSettingsState) {
  if (state.mode === 'record' && state.activePorId) {
    return resolvePorRecordPageVisibility(state.workspaceSettings, state.recordSettings);
  }
  return resolvePorRecordPageVisibility(state.workspaceSettings, []);
}

export const usePorPageSettingsStore = create<PorPageSettingsState>((set, get) => ({
  workspaceSettings: [],
  recordSettings: [],
  mode: 'workspace',
  activePorId: null,
  loading: false,
  error: null,
  loadedWorkspaceId: null,
  loadedPorId: null,

  fetchWorkspaceSettings: async (workspaceId) => {
    if (get().loadedWorkspaceId === workspaceId && get().workspaceSettings.length > 0) return;
    set({ loading: true, error: null });
    const { data, error } = await listPorPageSettings(workspaceId);
    set({
      workspaceSettings: data,
      loading: false,
      error,
      loadedWorkspaceId: workspaceId,
    });
  },

  fetchRecordSettings: async (porId, workspaceId) => {
    if (!get().loadedWorkspaceId || get().loadedWorkspaceId !== workspaceId) {
      await get().fetchWorkspaceSettings(workspaceId);
    }
    if (get().loadedPorId === porId && get().recordSettings.length > 0) {
      set({ mode: 'record', activePorId: porId });
      return;
    }
    set({ loading: true, error: null, mode: 'record', activePorId: porId });
    const { data, error } = await listPorRecordPageSettings(porId);
    set({
      recordSettings: data,
      loading: false,
      error,
      loadedPorId: porId,
    });
  },

  setMode: (mode, porId = null) => {
    set({ mode, activePorId: porId });
  },

  isVisible: (pageKey) => {
    const { visibility } = effectiveVisibility(get());
    return visibility.get(pageKey) !== false;
  },

  layoutSource: () => effectiveVisibility(get()).source,

  visibleSectionOrder: () => {
    const { visibility } = effectiveVisibility(get());
    return visiblePorSectionOrder(visibility);
  },

  defaultLandingSegment: () => {
    const { visibility } = effectiveVisibility(get());
    return defaultPorLandingSegment(visibility);
  },

  visiblePageCount: () => {
    const { visibility } = effectiveVisibility(get());
    return countVisiblePorPages(visibility);
  },

  toggleVisible: async (pageKey, workspaceId, porId = null) => {
    const state = get();
    const { visibility } = effectiveVisibility(state);
    const current = visibility.get(pageKey) !== false;
    const next = !current;

    if (current && countVisiblePorPages(visibility) <= 1) {
      set({ error: 'At least one Plan of Record page must remain visible.' });
      return;
    }

    set({ error: null });

    if (porId) {
      set((s) => {
        const existing = s.recordSettings.find((r) => r.pageKey === pageKey);
        if (existing) {
          return {
            recordSettings: s.recordSettings.map((r) =>
              r.pageKey === pageKey ? { ...r, isVisible: next } : r,
            ),
          };
        }
        return {
          recordSettings: [
            ...s.recordSettings,
            {
              id: `optimistic-${pageKey}`,
              porId,
              pageKey,
              isVisible: next,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            },
          ],
        };
      });

      const { error } = await setPorRecordPageVisible(porId, pageKey, next);
      if (error) {
        set((s) => ({
          recordSettings: s.recordSettings.map((r) =>
            r.pageKey === pageKey ? { ...r, isVisible: current } : r,
          ),
          error,
        }));
      }
      return;
    }

    set((s) => {
      const existing = s.workspaceSettings.find((r) => r.pageKey === pageKey);
      if (existing) {
        return {
          workspaceSettings: s.workspaceSettings.map((r) =>
            r.pageKey === pageKey ? { ...r, isVisible: next } : r,
          ),
        };
      }
      return {
        workspaceSettings: [
          ...s.workspaceSettings,
          {
            id: `optimistic-${pageKey}`,
            workspaceId,
            pageKey,
            isVisible: next,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        ],
      };
    });

    const { error } = await setPorPageVisible(workspaceId, pageKey, next);
    if (error) {
      set((s) => ({
        workspaceSettings: s.workspaceSettings.map((r) =>
          r.pageKey === pageKey ? { ...r, isVisible: current } : r,
        ),
        error,
      }));
    }
  },
}));
